import { ApiOutlined } from "@ant-design/icons";
import type { McpServer } from "@p0security/integrations-agentic-shared/interface";
import {
  Button,
  Empty,
  Flex,
  Input,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "@p0security/ui";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import { useHref } from "react-router-dom";

import {
  ErrorAlert,
  matchesSearch,
  useAgenticContext,
} from "./AgenticOverviewContext";

const SERVER_TYPE_LABELS: Record<McpServer["type"], string> = {
  container: "Container",
  upstream: "External",
  internal: "P0 managed",
};

const SERVER_COLUMNS: TableColumnsType<McpServer> = [
  {
    dataIndex: "id",
    key: "server",
    render: (id: string) => (
      <Flex align="center" gap={12}>
        <ApiOutlined
          className="text-lg text-[var(--ant-color-text-secondary)]"
          aria-hidden="true"
        />
        <Typography.Text strong>{id}</Typography.Text>
      </Flex>
    ),
    title: "Server",
    width: 260,
  },
  {
    dataIndex: "type",
    key: "type",
    render: (type: McpServer["type"]) => (
      <Tag color={type === "upstream" ? "gold" : "green"}>
        {SERVER_TYPE_LABELS[type]}
      </Tag>
    ),
    title: "Type",
    width: 160,
  },
  {
    dataIndex: "url",
    key: "url",
    render: (url: string) => (
      <Typography.Text
        className="break-all font-mono text-xs"
        copyable={{ text: url }}
      >
        {url}
      </Typography.Text>
    ),
    title: "Endpoint",
  },
];

const ServerTable: React.FC<{
  emptyDescription: string;
  loading: boolean;
  servers: McpServer[];
}> = ({ emptyDescription, loading, servers }) => (
  <div aria-label={loading ? "Loading MCP servers" : undefined}>
    <Table<McpServer>
      className="[&_.ant-table-tbody>tr>td]:align-middle"
      columns={SERVER_COLUMNS}
      dataSource={servers}
      loading={loading}
      locale={{
        emptyText: (
          <Empty
            description={emptyDescription}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ),
      }}
      pagination={false}
      rowKey="id"
      scroll={{ x: 720 }}
    />
  </div>
);

/** MCP servers tab. */
export const AgenticServers: React.FC = () => {
  const {
    canManageServers,
    onRefreshServers,
    servers,
    serversError,
    serversLoading,
  } = useAgenticContext();
  const [serverSearch, setServerSearch] = useState("");
  // Relative to this tab's fixed path (`agentic/servers`), so the router builds
  // the tenant path rather than hand-assembling it.
  const manageServersHref = useHref("../../integrations/agentic/server", {
    relative: "path",
  });

  const filteredServers = useMemo(
    () =>
      servers.filter((server) =>
        matchesSearch(serverSearch, [
          server.id,
          SERVER_TYPE_LABELS[server.type],
          server.url,
        ])
      ),
    [serverSearch, servers]
  );

  const updateServerSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setServerSearch(event.target.value),
    []
  );

  return (
    <section aria-label="Available MCP servers">
      <Flex gap={16} vertical>
        <Flex align="center" gap={12} justify="space-between" wrap>
          <div className="w-full max-w-xl">
            <Input.Search
              allowClear
              aria-label="Search MCP servers"
              className="w-full"
              placeholder="Search MCP servers"
              value={serverSearch}
              onChange={updateServerSearch}
            />
          </div>
          {canManageServers && (
            <Button href={manageServersHref} type="primary">
              Manage MCP servers
            </Button>
          )}
        </Flex>
        {serversError ? (
          <ErrorAlert messageText={serversError} onRetry={onRefreshServers} />
        ) : (
          <ServerTable
            emptyDescription={
              serverSearch.trim()
                ? "No MCP servers match your search."
                : "No MCP servers are available through this gateway."
            }
            loading={serversLoading}
            servers={filteredServers}
          />
        )}
      </Flex>
    </section>
  );
};
